import React, { useCallback, useRef, useState } from 'react';
import ForceGraph3D from 'react-force-graph-3d';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';

const Navigation3D = ({ onClose }) => {
  const navigate = useNavigate();
  const graphRef = useRef();
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());

  // ナビゲーションデータ
  const graphData = {
    nodes: [
      // メインページ
      { id: 'home', name: 'ホーム', val: 20, color: '#3b82f6', url: '/', group: 'main' },
      { id: 'services', name: 'サービス詳細', val: 15, color: '#10b981', url: '#features', group: 'main' },
      
      // 技術関連
      { id: 'technology', name: '技術概要', val: 15, color: '#8b5cf6', url: '/technology', group: 'tech' },
      { id: 'tech-core', name: 'コア技術', val: 12, color: '#7c3aed', url: '/technology/core', group: 'tech' },
      { id: 'tech-patents', name: '特許・知財', val: 12, color: '#6d28d9', url: '/technology/patents', group: 'tech' },
      { id: 'tech-demo', name: '技術デモ', val: 12, color: '#5b21b6', url: '/technology/demo', group: 'tech' },
      
      // ソリューション・事例
      { id: 'solutions', name: 'ソリューション', val: 15, color: '#f59e0b', url: '/solutions', group: 'solution' },
      { id: 'agriculture', name: '農業プラットフォーム', val: 12, color: '#d97706', url: '/agriculture', group: 'solution' },
      { id: 'cases', name: '導入事例', val: 12, color: '#ef4444', url: '/case-studies', group: 'solution' },
      
      // 企業情報
      { id: 'company', name: '企業情報', val: 15, color: '#06b6d4', url: '/company', group: 'company' },
      { id: 'mission', name: 'ミッション・ビジョン', val: 10, color: '#0891b2', url: '/company/mission', group: 'company' },
      { id: 'awards', name: '受賞歴', val: 10, color: '#0e7490', url: '/company/awards', group: 'company' },
      
      // リソース・サポート
      { id: 'resources', name: 'リソースセンター', val: 12, color: '#84cc16', url: '/resources', group: 'resource' },
      { id: 'faq', name: 'よくある質問', val: 8, color: '#65a30d', url: '/resources/faq', group: 'resource' },
      { id: 'glossary', name: '用語集', val: 8, color: '#4d7c0f', url: '/resources/glossary', group: 'resource' },
      
      // ニュース・IR
      { id: 'news', name: 'ニュース・IR', val: 12, color: '#f97316', url: '/news', group: 'news' },
      
      // 採用・パートナーシップ
      { id: 'careers', name: '採用情報', val: 12, color: '#ec4899', url: '/careers', group: 'business' },
      { id: 'partnership', name: 'パートナーシップ', val: 10, color: '#db2777', url: '/contact/partnership', group: 'business' },
      
      // お問い合わせ
      { id: 'contact', name: 'お問い合わせ', val: 15, color: '#6366f1', url: '/contact', group: 'contact' },
    ],
    links: [
      // ホームからメインカテゴリへ
      { source: 'home', target: 'services' },
      { source: 'home', target: 'technology' },
      { source: 'home', target: 'solutions' },
      { source: 'home', target: 'company' },
      { source: 'home', target: 'resources' },
      { source: 'home', target: 'news' },
      { source: 'home', target: 'careers' },
      { source: 'home', target: 'contact' },
      
      // 技術関連の内部リンク
      { source: 'technology', target: 'tech-core' },
      { source: 'technology', target: 'tech-patents' },
      { source: 'technology', target: 'tech-demo' },
      { source: 'tech-core', target: 'tech-patents' },
      
      // ソリューション関連
      { source: 'solutions', target: 'agriculture' },
      { source: 'solutions', target: 'cases' },
      { source: 'agriculture', target: 'cases' },
      
      // 企業情報関連
      { source: 'company', target: 'mission' },
      { source: 'company', target: 'awards' },
      { source: 'mission', target: 'awards' },
      
      // リソース関連
      { source: 'resources', target: 'faq' },
      { source: 'resources', target: 'glossary' },
      { source: 'faq', target: 'glossary' },
      
      // ビジネス関連
      { source: 'careers', target: 'partnership' },
      { source: 'partnership', target: 'contact' },
      
      // クロスカテゴリリンク
      { source: 'technology', target: 'solutions' },
      { source: 'solutions', target: 'contact' },
      { source: 'company', target: 'careers' },
      { source: 'resources', target: 'news' },
      { source: 'cases', target: 'contact' },
      { source: 'tech-demo', target: 'solutions' }
    ]
  };

  // ノードクリック処理
  const handleNodeClick = useCallback((node) => {
    if (node.url.startsWith('#')) {
      // アンカーリンクの場合
      const element = document.querySelector(node.url);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        onClose();
      }
    } else {
      // ページ遷移の場合
      navigate(node.url);
      onClose();
    }
  }, [navigate, onClose]);

  // ノードホバー処理
  const handleNodeHover = useCallback((node) => {
    const highlightNodes = new Set();
    const highlightLinks = new Set();

    if (node) {
      highlightNodes.add(node);
      // 関連するリンクとノードをハイライト
      graphData.links.forEach(link => {
        if (link.source === node.id || link.target === node.id) {
          highlightLinks.add(link);
          highlightNodes.add(link.source);
          highlightNodes.add(link.target);
        }
      });
    }

    setHighlightNodes(highlightNodes);
    setHighlightLinks(highlightLinks);
  }, []);

  // ノードの色決定
  const getNodeColor = useCallback((node) => {
    return highlightNodes.has(node) ? '#ffffff' : node.color;
  }, [highlightNodes]);

  // リンクの色決定
  const getLinkColor = useCallback((link) => {
    return highlightLinks.has(link) ? '#ffffff' : 'rgba(255, 255, 255, 0.2)';
  }, [highlightLinks]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="relative w-full h-full">
        {/* 閉じるボタン */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 text-white text-2xl bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition-all"
        >
          ×
        </button>

        {/* タイトル */}
        <div className="absolute top-4 left-4 z-10 text-white">
          <h2 className="text-2xl font-bold mb-2">サイトナビゲーション</h2>
          <p className="text-sm opacity-80 mb-4">ノードをクリックしてページに移動</p>
          
          {/* グループレジェンド */}
          <div className="text-xs opacity-70">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3b82f6' }}></div>
                <span>メイン</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8b5cf6' }}></div>
                <span>技術</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }}></div>
                <span>ソリューション</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#06b6d4' }}></div>
                <span>企業情報</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#84cc16' }}></div>
                <span>リソース</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ec4899' }}></div>
                <span>ビジネス</span>
              </div>
            </div>
          </div>
        </div>

        {/* 3Dグラフ */}
        <ForceGraph3D
          ref={graphRef}
          graphData={graphData}
          nodeLabel="name"
          nodeVal="val"
          nodeColor={getNodeColor}
          linkColor={getLinkColor}
          linkWidth={2}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          backgroundColor="rgba(0, 0, 0, 0)"
          showNavInfo={false}
          controlType="orbit"
          
          // 自動フィット設定
          onEngineStop={() => {
            if (graphRef.current) {
              // グラフ全体を画面にフィット
              graphRef.current.zoomToFit(1000, 50); // 1000ms、50pxのパディング
            }
          }}
          
          nodeThreeObject={(node) => {
            const sprite = new THREE.Sprite(
              new THREE.SpriteMaterial({
                map: createTextTexture(node.name),
                transparent: true
              })
            );
            sprite.scale.set(25, 12, 1); // 適度なサイズ
            return sprite;
          }}
          
          linkDirectionalParticles={2}
          linkDirectionalParticleSpeed={0.01}
          linkDirectionalParticleWidth={2}
          linkDirectionalParticleColor={() => 'rgba(255, 255, 255, 0.8)'}
          nodeAutoColorBy="group"
          d3AlphaDecay={0.01}
          d3VelocityDecay={0.4}
          warmupTicks={150}
          cooldownTicks={300}
          d3Force={{
            link: { distance: 100 },
            charge: { strength: -500 },
            center: { strength: 0.3 } // 中央寄せを強化
          }}
          enableNodeDrag={true}
          nodeResolution={16}
          linkOpacity={0.4}
        />

        {/* 操作説明 */}
        <div className="absolute bottom-4 left-4 text-white text-sm opacity-80">
          <p>ドラッグ: 回転 | スクロール: ズーム | クリック: 移動</p>
        </div>
      </div>
    </div>
  );
};

// テキストテクスチャ作成関数
const createTextTexture = (text) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 320;
  canvas.height = 160;

  // 背景
  context.fillStyle = 'rgba(0, 0, 0, 0.8)';
  context.fillRect(0, 0, canvas.width, canvas.height);

  // テキスト
  context.fillStyle = 'white';
  context.font = 'bold 24px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
};

export default Navigation3D; 